import { useEffect } from 'react';
import { SetURLSearchParams } from 'react-router';

import { resolveTabIndex } from '~utils/TabRouting';

/**
 * Resolves the `show` parameter to a tab index, rewriting the URL when it does not already spell
 * that tab so a bogus value can't be bookmarked or shared. Replaces rather than pushes, keeps
 * sibling parameters, and leaves an already-correct (or absent) parameter alone.
 */
export const useNormalisedTabIndex = (
  viewTabs: Record<string, string | number>,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  defaultTab: number,
): number => {
  const showParam = searchParams.get('show');
  const tabIndex = resolveTabIndex(viewTabs, showParam, defaultTab);
  const resolvedShowParam = String(viewTabs[tabIndex]);

  useEffect(() => {
    if (showParam === resolvedShowParam) {
      return;
    }

    // Updater form rather than a plain object, so sibling parameters survive the rewrite.
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('show', resolvedShowParam);

        return next;
      },
      { replace: true },
    );
  }, [showParam, resolvedShowParam]);

  return tabIndex;
};
